import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TablePagination,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	Stack,
	Typography,
	Box,
	CircularProgress,
	TextField,
} from "@mui/material";
import { useGetActionHistoryQuery } from "InstanceManagement/Api/Queries/useGetActionHistoryQuery";
import { ActionType } from "Infrastructure/Api/ApiCustom";
import { format } from "date-fns";
import { BlModal } from "Shared/BlModal";
import { BlDefaultButton } from "Shared/Buttons/BlDefaultButton";
import { BlTooltip } from "Shared/BlTooltip";
import { getActionTypeDescription } from "Infrastructure/Utils/Nsure/DeviceUtils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";

const ActionHistoryTable: React.FC = () => {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(15);
	const [selectedTypes, setSelectedTypes] = useState<ActionType[]>([]);
	const [requestIdInput, setRequestIdInput] = useState("");
	const [debouncedRequestId, setDebouncedRequestId] = useState("");
	const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);
	const [currentJsonContent, setCurrentJsonContent] = useState("");
	const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
	const [currentResponseContent, setCurrentResponseContent] = useState("");

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedRequestId(requestIdInput);
		}, 500);

		return () => clearTimeout(timer);
	}, [requestIdInput]);

	const typesString = useMemo(() => {
		return selectedTypes.map((type) => type.toString()).join(";");
	}, [selectedTypes]);

	const queryParams = useMemo(
		() => ({
			offset: page * rowsPerPage,
			limit: rowsPerPage,
			types: typesString,
			device: undefined,
			requestId: debouncedRequestId || undefined,
		}),
		[page, rowsPerPage, typesString, debouncedRequestId]
	);

	const { data, isLoading, error } = useGetActionHistoryQuery(
		queryParams.offset,
		queryParams.limit,
		queryParams.types,
		queryParams.device,
		queryParams.requestId
	);

	const handleChangePage = useCallback((event: unknown, newPage: number) => {
		setPage(newPage);
	}, []);

	const handleChangeRowsPerPage = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setRowsPerPage(parseInt(event.target.value, 10));
			setPage(0);
		},
		[]
	);

	const handleTypeChange = useCallback((event: any) => {
		const value = event.target.value;
		setSelectedTypes(typeof value === "string" ? [] : value);
	}, []);

	const handleClearTypes = useCallback(() => {
		setSelectedTypes([]);
	}, []);

	const handleRequestIdChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setRequestIdInput(event.target.value);
		},
		[]
	);

	const handleClearRequestId = useCallback(() => {
		setRequestIdInput("");
	}, []);

	const getStatusChip = (isSuccess: boolean) => {
		return (
			<Chip
				label={isSuccess ? "Úspěch" : "Chyba"}
				color={isSuccess ? "success" : "error"}
				size="small"
				sx={{ fontSize: "0.75rem", height: 20 }}
			/>
		);
	};

	const handleShowJsonModal = (messageJson: string) => {
		let formattedJson = messageJson;
		try {
			// Try to parse and format the JSON for better readability
			const parsed = JSON.parse(messageJson);
			formattedJson = JSON.stringify(parsed, null, 2);
		} catch {
			// If it's not valid JSON, just show the original string
			formattedJson = messageJson;
		}
		setCurrentJsonContent(formattedJson);
		setIsJsonModalOpen(true);
	};

	const handleCloseJsonModal = () => {
		setIsJsonModalOpen(false);
		setCurrentJsonContent("");
	};

	const handleShowResponseMessageModal = (responseResultText: string) => {
		const formattedContent = responseResultText
			.split(";")
			.map((item) => item.trim())
			.filter((item) => item.length > 0)
			.join("\n");

		setCurrentResponseContent(formattedContent);
		setIsResponseModalOpen(true);
	};

	const handleCloseResponseModal = () => {
		setIsResponseModalOpen(false);
		setCurrentResponseContent("");
	};

	const handleCopyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
		} catch (err) {
			console.error("Failed to copy to clipboard:", err);
		}
	};

	const textFieldProps = useMemo(
		() => ({
			size: "small" as const,
			label: "Request ID",
			value: requestIdInput,
			onChange: handleRequestIdChange,
			sx: { width: 300 },
		}),
		[requestIdInput, handleRequestIdChange]
	);

	const handleClearAll = useCallback(() => {
		handleClearTypes();
		handleClearRequestId();
	}, [handleClearTypes, handleClearRequestId]);

	if (isLoading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight={400}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight={400}
			>
				<Typography color="error">
					Chyba při načítání dat: {error.message}
				</Typography>
			</Box>
		);
	}

	const actionHistoryData =
		data && data.status === 200 ? (data.data as any) : null;
	const totalCount = actionHistoryData?.totalCount || 0;

	return (
		<Stack spacing={2}>
			<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
				<TextField {...textFieldProps} />
				<FormControl size="small" sx={{ width: 640 }}>
					<InputLabel>Typy akcí</InputLabel>
					<Select
						multiple
						value={selectedTypes}
						onChange={handleTypeChange}
						label="Typy akcí"
						renderValue={(selected) => (
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
								{selected.map((value) => (
									<Chip
										key={value}
										label={getActionTypeDescription(value)}
										size="small"
									/>
								))}
							</Box>
						)}
					>
						{Object.values(ActionType)
							.filter((value) => typeof value === "number")
							.map((actionType) => (
								<MenuItem key={actionType} value={actionType}>
									{getActionTypeDescription(actionType as ActionType)}
								</MenuItem>
							))}
					</Select>
				</FormControl>
				{(selectedTypes.length > 0 || requestIdInput) && (
					<BlDefaultButton
						size="small"
						onClick={handleClearAll}
						title="Zrušit všechny filtry"
					>
						Zrušit
					</BlDefaultButton>
				)}
			</Stack>

			<TableContainer component={Paper}>
				<Table
					size="small"
					sx={{ "& .MuiTableCell-root": { padding: "8px 12px" } }}
				>
					<TableHead>
						<TableRow>
							<TableCell>RequestID</TableCell>
							<TableCell>Datum</TableCell>
							<TableCell>Typ akce</TableCell>
							<TableCell>Instance</TableCell>
							<TableCell>Request – JSON</TableCell>
							<TableCell>Request – Stav</TableCell>
							<TableCell>Request – Message</TableCell>
							<TableCell>Response – Stav</TableCell>
							<TableCell>Response – Message</TableCell>
							<TableCell>Provedl</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{actionHistoryData?.items?.map((row: any) => (
							<TableRow key={row.actionID}>
								<TableCell>
									<Chip
										label={
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
											>
												<span>{row.requestID}</span>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														handleCopyToClipboard(row.requestID);
													}}
													sx={{
														padding: 0,
														"& .MuiSvgIcon-root": {
															fontSize: "0.7rem",
														},
													}}
												>
													<ContentCopyIcon />
												</IconButton>
											</Box>
										}
										size="small"
										variant="outlined"
										sx={{ fontSize: "0.7rem", height: 18 }}
									/>
								</TableCell>
								<TableCell>
									<Chip
										label={format(
											new Date(row.dateCreated),
											"dd.MM.yyyy HH:mm:ss"
										)}
										size="small"
										variant="outlined"
										sx={{ fontSize: "0.7rem", height: 18 }}
									/>
								</TableCell>
								<TableCell>
									{getActionTypeDescription(row.actionType)}
								</TableCell>
								<TableCell>
									{row.filteredDeviceNames ? (
										<Box
											sx={{
												display: "flex",
												flexWrap: "wrap",
												gap: 0.5,
												alignItems: "center",
											}}
										>
											{row.filteredDeviceNames
												.split(";")
												.map((device: string) => device.trim())
												.filter((device: string) => device.length > 0)
												.slice(0, 5)
												.map((device: string) => (
													<Chip
														key={device}
														label={device}
														size="small"
														variant="outlined"
														sx={{ fontSize: "0.7rem", height: 18 }}
													/>
												))}
											{(() => {
												const devices = row.filteredDeviceNames
													.split(";")
													.map((device: string) => device.trim())
													.filter((device: string) => device.length > 0);
												const remainingCount = devices.length - 5;

												if (remainingCount > 0) {
													return (
														<BlTooltip
															title={
																<Box
																	sx={{
																		display: "flex",
																		flexWrap: "wrap",
																		gap: 0.5,
																	}}
																>
																	{devices
																		.slice(5)
																		.map((device: string) => (
																			<Chip
																				key={device}
																				label={device}
																				size="small"
																				variant="outlined"
																				sx={{ fontSize: "0.7rem", height: 18 }}
																			/>
																		))}
																</Box>
															}
															placement="top"
															componentsProps={{
																tooltip: {
																	sx: {
																		bgcolor: "background.paper",
																		color: "text.primary",
																		border: "1px solid",
																		borderColor: "divider",
																		boxShadow: 3,
																		minWidth: "300px",
																		maxWidth: "800px",
																	},
																},
															}}
														>
															<Typography
																variant="caption"
																sx={{
																	color: "text.secondary",
																	cursor: "help",
																	fontSize: "0.7rem",
																	ml: 0.5,
																}}
															>
																+{remainingCount}
															</Typography>
														</BlTooltip>
													);
												}
												return null;
											})()}
										</Box>
									) : (
										"-"
									)}
								</TableCell>
								<TableCell>
									<BlDefaultButton
										onClick={() => handleShowJsonModal(row.messageJson)}
										disabled={!row.messageJson}
										size="small"
									>
										Zobrazit
									</BlDefaultButton>
								</TableCell>
								<TableCell>{getStatusChip(row.requestIsSuccess)}</TableCell>
								<TableCell>{row.requestResultText || "-"}</TableCell>
								<TableCell>
									{row.responseIsSuccess !== null
										? getStatusChip(row.responseIsSuccess)
										: "-"}
								</TableCell>
								<TableCell>
									{row.responseResultText ? (
										<BlDefaultButton
											size="small"
											onClick={() =>
												handleShowResponseMessageModal(row.responseResultText)
											}
										>
											Zobrazit
										</BlDefaultButton>
									) : (
										"-"
									)}
								</TableCell>
								<TableCell>{row.createdByName || "-"}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<TablePagination
				rowsPerPageOptions={[5, 10, 15, 20, 50]}
				component="div"
				count={totalCount}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				labelRowsPerPage="Řádků na stránku:"
				labelDisplayedRows={({ from, to, count }) =>
					`${from}-${to} z ${count !== -1 ? count : `více než ${to}`}`
				}
				size="small"
			/>

			{isJsonModalOpen && (
				<BlModal handleClose={handleCloseJsonModal}>
					<Box sx={{ position: "relative" }}>
						<Box
							sx={{
								position: "absolute",
								top: 8,
								right: 8,
								zIndex: 1,
							}}
						>
							<IconButton
								onClick={() => handleCopyToClipboard(currentJsonContent)}
								sx={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
							>
								<ContentCopyIcon sx={{ fontSize: "1rem" }} />
							</IconButton>
						</Box>
						<Box
							sx={{
								maxHeight: "70vh",
								overflow: "auto",
								"&::-webkit-scrollbar": {
									width: "8px",
								},
								"&::-webkit-scrollbar-track": {
									background: "#f1f1f1",
									borderRadius: "4px",
								},
								"&::-webkit-scrollbar-thumb": {
									background: "#c1c1c1",
									borderRadius: "4px",
									"&:hover": {
										background: "#a8a8a8",
									},
								},
							}}
						>
							<Typography
								variant="body2"
								component="pre"
								sx={{
									fontFamily: "monospace",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
									backgroundColor: "grey.100",
									padding: 2,
									borderRadius: 1,
									border: "1px solid",
									borderColor: "grey.300",
								}}
							>
								{currentJsonContent}
							</Typography>
						</Box>
					</Box>
				</BlModal>
			)}

			{isResponseModalOpen && (
				<BlModal handleClose={handleCloseResponseModal}>
					<Box sx={{ position: "relative" }}>
						<Box
							sx={{
								position: "absolute",
								top: 8,
								right: 8,
								zIndex: 1,
							}}
						>
							<IconButton
								onClick={() => handleCopyToClipboard(currentResponseContent)}
								sx={{ backgroundColor: "rgba(255, 255, 255, 0.9)" }}
							>
								<ContentCopyIcon sx={{ fontSize: "1rem" }} />
							</IconButton>
						</Box>
						<Box
							sx={{
								maxHeight: "70vh",
								minWidth: "35vw",
								overflow: "auto",
								"&::-webkit-scrollbar": {
									width: "8px",
								},
								"&::-webkit-scrollbar-track": {
									background: "#f1f1f1",
									borderRadius: "4px",
								},
								"&::-webkit-scrollbar-thumb": {
									background: "#c1c1c1",
									borderRadius: "4px",
									"&:hover": {
										background: "#a8a8a8",
									},
								},
							}}
						>
							<Typography
								variant="body2"
								component="pre"
								sx={{
									fontFamily: "monospace",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
									backgroundColor: "grey.100",
									padding: 2,
									borderRadius: 1,
									border: "1px solid",
									borderColor: "grey.300",
								}}
							>
								{currentResponseContent}
							</Typography>
						</Box>
					</Box>
				</BlModal>
			)}
		</Stack>
	);
};

export default ActionHistoryTable;
